/**
 * RiadDetailPage - Vue de pilotage opérationnelle d'un tenant
 * 
 * Affiche toutes les informations nécessaires pour piloter un riad:
 * - Client SaaS associé
 * - Plan / Abonnement
 * - Statuts (riad, setup, config)
 * - Modules activés
 * - Utilisateurs liés
 * - Conciergerie associée
 * - Activité récente / audit
 * 
 * Actions disponibles:
 * - Ouvrir Tenant (impersonation)
 * - Réparer configs
 * - Suspendre / Réactiver
 * - Accès rapide utilisateurs / abonnement
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  Settings,
  Activity,
  Calendar,
  CreditCard,
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ExternalLink,
  Wrench,
  Play,
  Pause,
  ArrowLeft,
  RefreshCw,
  Package,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  TrendingUp,
  Globe,
  Layers,
  LayoutGrid,
  UserCheck,
  AlertCircle,
  Heart,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge helper
const StatusBadge = ({ status, size = 'default' }) => {
  const configs = {
    ACTIVE: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Actif' },
    SUSPENDED: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Suspendu' },
    DISABLED: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Désactivé' },
    TRIAL: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Essai' },
    EXPIRED: { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Expiré' },
    LIFETIME: { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'À vie' },
    PENDING: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'En attente' },
    IN_PROGRESS: { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'En cours' },
    COMPLETED: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Terminé' },
    OK: { color: 'bg-green-100 text-green-800 border-green-200', label: 'OK' },
    INCOMPLETE: { color: 'bg-orange-100 text-orange-800 border-orange-200', label: 'Incomplet' },
    UNKNOWN: { color: 'bg-gray-100 text-gray-800 border-gray-200', label: 'Inconnu' },
    OWNER: { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', label: 'Propriétaire' },
    CONCIERGERIE: { color: 'bg-cyan-100 text-cyan-800 border-cyan-200', label: 'Conciergerie' }
  };
  const config = configs[status] || configs.UNKNOWN;
  return (
    <Badge variant="outline" className={`${config.color} ${size === 'sm' ? 'text-xs px-1.5 py-0' : ''}`}>
      {config.label}
    </Badge>
  );
};

// Progress ring component
const ProgressRing = ({ percent, size = 60, strokeWidth = 6, color = '#22C55E' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        stroke="#E5E7EB"
        fill="transparent"
        strokeWidth={strokeWidth}
        r={radius}
        cx={size / 2}
        cy={size / 2}
      />
      <circle
        stroke={color}
        fill="transparent"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        r={radius}
        cx={size / 2}
        cy={size / 2}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
    </svg>
  );
};

// Quick stat card
const QuickStat = ({ icon: Icon, label, value, subValue, color = 'blue' }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600'
  };
  
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
      <div className={`p-2 rounded-lg ${colors[color]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
        {subValue && <p className="text-xs text-gray-400">{subValue}</p>}
      </div>
    </div>
  );
};

export default function RiadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [showOpenTenantDialog, setShowOpenTenantDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showRepairDialog, setShowRepairDialog] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/riads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const result = await response.json();
      setData(result);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // ============ ACTIONS ============
  
  const handleOpenTenant = async () => {
    setActionLoading(true);
    try {
      // Log impersonation
      await fetch(`${API_URL}/api/admin/audit/log`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'IMPERSONATE_TENANT',
          entity_type: 'Riad',
          entity_id: data.riad.id,
          description: `Ouverture du tenant: ${data.riad.name}`,
          metadata: {
            riad_name: data.riad.name,
            riad_slug: data.riad.slug,
            account_name: data.saasAccount?.name
          }
        })
      });
      
      // Store context
      localStorage.setItem('impersonated_riad', JSON.stringify({
        id: data.riad.id,
        name: data.riad.name,
        slug: data.riad.slug
      }));
      
      // Open in new tab
      window.open(`/dashboard?riad_id=${data.riad.id}`, '_blank');
      toast.success(`Tenant "${data.riad.name}" ouvert dans un nouvel onglet`);
      setShowOpenTenantDialog(false);
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du tenant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    setActionLoading(true);
    try {
      const endpoint = data.riad.status === 'SUSPENDED' ? 'reactivate' : 'suspend';
      const response = await fetch(`${API_URL}/api/admin/riads/${id}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reason: 'Action depuis la fiche riad' })
      });
      
      if (!response.ok) throw new Error('Erreur lors de l\'action');
      
      toast.success(endpoint === 'suspend' ? 'Riad suspendu' : 'Riad réactivé');
      setShowSuspendDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRepairConfigs = async (dryRun = true) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/riads/${id}/repair-configs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ dry_run: dryRun })
      });
      
      if (!response.ok) throw new Error('Erreur lors de la réparation');
      
      const result = await response.json();
      setRepairResult(result);
      
      if (!dryRun) {
        toast.success(`${result.configs_created || 0} configuration(s) réparée(s)`);
        fetchData();
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
        <p className="text-gray-600">Riad non trouvé</p>
        <Button variant="outline" onClick={() => navigate('/admin/riads')} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour à la liste
        </Button>
      </div>
    );
  }

  const { riad, saasAccount, subscription, users, modules, config_status, activity, stats, setupChecklist, healthIndicators, actions, conciergeAccount, configsByCategory, planModules } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/admin/riads')}
            className="mt-1"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{riad.name}</h1>
              <StatusBadge status={riad.status} />
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {riad.slug && <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">{riad.slug}</span>}
              {riad.email && <span className="ml-2">{riad.email}</span>}
            </p>
            {saasAccount && (
              <Link 
                to={`/admin/customers/${saasAccount.id}`}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
              >
                <Building2 className="h-3 w-3" />
                Client: {saasAccount.name}
                <ChevronRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {actions?.can_open_tenant && (
            <Button 
              onClick={() => setShowOpenTenantDialog(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="open-tenant-btn"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ouvrir Tenant
            </Button>
          )}
          {actions?.can_repair_configs && (
            <Button 
              variant="outline" 
              onClick={() => {
                setRepairResult(null);
                setShowRepairDialog(true);
                handleRepairConfigs(true);
              }}
              data-testid="repair-configs-btn"
            >
              <Wrench className="h-4 w-4 mr-2" />
              Réparer Configs
            </Button>
          )}
          {actions?.can_suspend && (
            <Button 
              variant="outline" 
              onClick={() => setShowSuspendDialog(true)}
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
              data-testid="suspend-btn"
            >
              <Pause className="h-4 w-4 mr-2" />
              Suspendre
            </Button>
          )}
          {actions?.can_reactivate && (
            <Button 
              variant="outline" 
              onClick={() => setShowSuspendDialog(true)}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid="reactivate-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              Réactiver
            </Button>
          )}
        </div>
      </div>

      {/* Alert Banner */}
      {(subscription?.is_expiring_soon || config_status?.status === 'INCOMPLETE' || riad.status === 'SUSPENDED') && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
            <div className="space-y-1">
              {subscription?.is_expiring_soon && (
                <p className="text-sm text-orange-800">
                  Abonnement expire dans <strong>{subscription.days_remaining} jours</strong>
                </p>
              )}
              {config_status?.status === 'INCOMPLETE' && (
                <p className="text-sm text-orange-800">
                  <strong>{config_status.issues_count}</strong> configuration(s) manquante(s)
                </p>
              )}
              {riad.status === 'SUSPENDED' && (
                <p className="text-sm text-orange-800">Ce riad est <strong>suspendu</strong></p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <QuickStat 
          icon={Users} 
          label="Utilisateurs" 
          value={stats.users}
          subValue={`${stats.active_users} actifs`}
          color="blue"
        />
        <QuickStat 
          icon={Calendar} 
          label="Réservations" 
          value={stats.reservations}
          subValue={`${stats.reservations_this_month} ce mois`}
          color="green"
        />
        <QuickStat 
          icon={Settings} 
          label="Configs" 
          value={stats.configs}
          subValue={`${stats.config_categories} catégories`}
          color="purple"
        />
        <QuickStat 
          icon={Package} 
          label="Produits" 
          value={stats.products}
          color="orange"
        />
        <QuickStat 
          icon={UserCheck} 
          label="Employés" 
          value={stats.employees}
          color="blue"
        />
        <QuickStat 
          icon={User} 
          label="Clients" 
          value={stats.guests}
          color="green"
        />
      </div>

      {/* Health & Progress indicators */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Setup Progress */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Setup</p>
                <p className="text-2xl font-bold">{riad.setup_progress}</p>
                <StatusBadge status={riad.setup_state} size="sm" />
              </div>
              <div className="relative">
                <ProgressRing 
                  percent={riad.setup_progress_percent} 
                  color={riad.setup_progress_percent === 100 ? '#22C55E' : '#F59E0B'}
                />
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                  {riad.setup_progress_percent}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Config Status */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Configurations</p>
                <p className="text-2xl font-bold">{config_status.total_configs}</p>
                <StatusBadge status={config_status.status} size="sm" />
              </div>
              <div className="text-right">
                {config_status.issues_count > 0 && (
                  <p className="text-sm text-orange-600">{config_status.issues_count} problèmes</p>
                )}
                <p className="text-xs text-gray-400">{config_status.audited_count} audités</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Health Score */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Santé</p>
                <p className="text-2xl font-bold">{riad.health_score}</p>
              </div>
              <div className="relative">
                <ProgressRing 
                  percent={riad.health_percent} 
                  color={riad.health_percent >= 80 ? '#22C55E' : riad.health_percent >= 60 ? '#F59E0B' : '#EF4444'}
                />
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">
                  {riad.health_percent}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">Abonnement</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">Modules</TabsTrigger>
          <TabsTrigger value="configs" data-testid="tab-configs">Configs</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activité</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Riad Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informations Riad
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Nom</span>
                  <span className="font-medium">{riad.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Slug</span>
                  <span className="font-mono text-sm">{riad.slug}</span>
                </div>
                {riad.email && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span>{riad.email}</span>
                  </div>
                )}
                {riad.phone && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Téléphone</span>
                    <span>{riad.phone}</span>
                  </div>
                )}
                {riad.city && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ville</span>
                    <span>{riad.city}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Devise</span>
                  <span>{riad.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Créé le</span>
                  <span>{new Date(riad.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
              </CardContent>
            </Card>

            {/* SaaS Account */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Client SaaS
                </CardTitle>
              </CardHeader>
              <CardContent>
                {saasAccount ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Compte</span>
                      <Link 
                        to={`/admin/customers/${saasAccount.id}`}
                        className="text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {saasAccount.name}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type</span>
                      <StatusBadge status={saasAccount.type} size="sm" />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Statut</span>
                      <StatusBadge status={saasAccount.status} size="sm" />
                    </div>
                    {saasAccount.email && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email</span>
                        <span className="text-sm">{saasAccount.email}</span>
                      </div>
                    )}
                    {saasAccount.invoiceSummary && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-gray-500 mb-2">Facturation</p>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-green-600">{saasAccount.invoiceSummary.paid} payées</div>
                          <div className="text-orange-600">{saasAccount.invoiceSummary.pending} en attente</div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Aucun compte SaaS associé</p>
                )}
              </CardContent>
            </Card>

            {/* Concierge */}
            {conciergeAccount && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Conciergerie
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nom</span>
                    <span className="font-medium">{conciergeAccount.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Statut</span>
                    <StatusBadge status={conciergeAccount.status} size="sm" />
                  </div>
                  {conciergeAccount.email && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email</span>
                      <span className="text-sm">{conciergeAccount.email}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Setup Checklist */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Checklist Setup
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(setupChecklist).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      {value ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Abonnement actif</CardTitle>
            </CardHeader>
            <CardContent>
              {subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div 
                      className="w-3 h-12 rounded-full" 
                      style={{ backgroundColor: subscription.plan_color }}
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-bold">{subscription.plan}</h3>
                      <p className="text-sm text-gray-500">{subscription.plan_code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">
                        {subscription.monthly_price} {subscription.currency}
                      </p>
                      <StatusBadge status={subscription.status} size="sm" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Début</p>
                      <p className="font-medium">
                        {subscription.start_date ? new Date(subscription.start_date).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Fin</p>
                      <p className={`font-medium ${subscription.is_expiring_soon ? 'text-orange-600' : ''}`}>
                        {subscription.end_date ? new Date(subscription.end_date).toLocaleDateString('fr-FR') : 'Illimité'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Jours restants</p>
                      <p className={`font-medium ${subscription.days_remaining < 30 ? 'text-orange-600' : ''}`}>
                        {subscription.days_remaining ?? '∞'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Renouvellement auto</p>
                      <p className="font-medium">{subscription.auto_renew ? 'Oui' : 'Non'}</p>
                    </div>
                  </div>
                  
                  {subscription.trial_end_date && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Période d'essai</strong> jusqu'au {new Date(subscription.trial_end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun abonnement actif</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">{users.length} Utilisateurs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {users.map((access) => (
                  <div 
                    key={access.access_id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        access.is_owner ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {access.user?.first_name?.[0]}{access.user?.last_name?.[0]}
                      </div>
                      <div>
                        <p className="font-medium">{access.user?.full_name}</p>
                        <p className="text-xs text-gray-500">{access.user?.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{access.role}</Badge>
                      {access.is_owner && <Badge className="bg-indigo-100 text-indigo-800">Propriétaire</Badge>}
                      {!access.is_active && <Badge variant="destructive">Inactif</Badge>}
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Aucun utilisateur</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Modules activés</CardTitle>
              <CardDescription>Configuration des modules pour ce tenant</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(modules).map(([key, enabled]) => (
                  <div 
                    key={key}
                    className={`p-3 rounded-lg border ${enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center gap-2">
                      {enabled ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-400" />
                      )}
                      <span className={`text-sm font-medium ${enabled ? 'text-green-800' : 'text-gray-500'}`}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {Object.keys(modules).length === 0 && (
                <p className="text-center text-gray-500 py-8">Aucun module configuré</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configs Tab */}
        <TabsContent value="configs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Configurations par catégorie</CardTitle>
                  <CardDescription>
                    {config_status.total_configs} configurations au total
                    {config_status.issues_count > 0 && (
                      <span className="text-orange-600"> - {config_status.issues_count} problème(s)</span>
                    )}
                  </CardDescription>
                </div>
                <StatusBadge status={config_status.status} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(configsByCategory).map(([category, data]) => (
                  <div 
                    key={category}
                    className="p-3 rounded-lg border bg-gray-50"
                  >
                    <p className="text-xs text-gray-500 uppercase tracking-wider">{category}</p>
                    <p className="text-xl font-bold">{data.count}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <span>{data.active} actives</span>
                      <span>•</span>
                      <span>{data.system} système</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {config_status.issues?.length > 0 && (
                <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                  <h4 className="font-medium text-orange-800 mb-2">Problèmes détectés</h4>
                  <ul className="space-y-1 text-sm text-orange-700">
                    {config_status.issues.slice(0, 5).map((issue, i) => (
                      <li key={i}>
                        • {typeof issue === 'object' 
                          ? `${issue.category || 'Config'}: ${issue.code || issue.type || 'Problème'} - ${issue.missing_fields?.join(', ') || 'Configuration incomplète'}`
                          : issue}
                      </li>
                    ))}
                    {config_status.total_issues > 5 && (
                      <li className="text-orange-600">... et {config_status.total_issues - 5} autres</li>
                    )}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Activité récente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activity.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded">
                    <Activity className="h-4 w-4 text-gray-400 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{log.action}</p>
                      {log.description && (
                        <p className="text-xs text-gray-500 truncate">{log.description}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        {log.user} • {new Date(log.createdAt).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
                {activity.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Aucune activité récente</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      
      {/* Open Tenant Dialog */}
      <Dialog open={showOpenTenantDialog} onOpenChange={setShowOpenTenantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ouvrir le tenant</DialogTitle>
            <DialogDescription>
              Vous allez accéder au tenant "{riad.name}" en mode impersonation. Cette action est enregistrée dans l'audit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenTenantDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleOpenTenant} disabled={actionLoading}>
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Ouvrir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend/Reactivate Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {riad.status === 'SUSPENDED' ? 'Réactiver le riad' : 'Suspendre le riad'}
            </DialogTitle>
            <DialogDescription>
              {riad.status === 'SUSPENDED' 
                ? 'Le riad sera réactivé et les utilisateurs pourront à nouveau y accéder.'
                : 'Le riad sera suspendu et les utilisateurs ne pourront plus y accéder.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSuspend} 
              disabled={actionLoading}
              variant={riad.status === 'SUSPENDED' ? 'default' : 'destructive'}
            >
              {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              {riad.status === 'SUSPENDED' ? 'Réactiver' : 'Suspendre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Configs Dialog */}
      <Dialog open={showRepairDialog} onOpenChange={setShowRepairDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Réparer les configurations</DialogTitle>
            <DialogDescription>
              Cette action va créer les configurations système manquantes pour ce tenant.
            </DialogDescription>
          </DialogHeader>
          
          {actionLoading && !repairResult && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          )}
          
          {repairResult && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${repairResult.dry_run ? 'bg-blue-50' : 'bg-green-50'}`}>
                <p className={`font-medium ${repairResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                  {repairResult.message}
                </p>
              </div>
              
              {repairResult.categories_fixed?.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Catégories à réparer:</p>
                  <div className="flex flex-wrap gap-1">
                    {repairResult.categories_fixed.map(cat => (
                      <Badge key={cat} variant="outline">{cat}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepairDialog(false)}>
              Fermer
            </Button>
            {repairResult?.dry_run && (repairResult.configs_to_create > 0 || repairResult.configs_created === undefined) && (
              <Button onClick={() => handleRepairConfigs(false)} disabled={actionLoading}>
                {actionLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Wrench className="h-4 w-4 mr-2" />}
                Appliquer les réparations
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
